import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { isValidationApiError } from "../api/errors";
import type { DocumentSubstitution } from "../api/generated";
import { shellBootstrapKey, shellBootstrapQuery } from "./bootstrap";
import { TextField } from "./CounterpartyFields";
import { useSubmissionKey } from "./counterpartiesSupport";
import { Notice } from "./Notice";
import { Select } from "./Select";
import type { ShellServices } from "./services";
import {
  localDateTime,
  substitutionError,
  substitutionPeriodErrors,
  substitutionReasons,
} from "./substitutionsSupport";

export function SubstitutionForm({
  userId,
  scopes,
  services,
  onSaved,
  onCancel,
}: {
  userId: string;
  scopes: readonly { id: string; name: string }[];
  services: ShellServices;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const bootstrap = useQuery(shellBootstrapQuery(services)).data;
  const client = useQueryClient();
  const initialScope =
    scopes.find((scope) => scope.id === bootstrap?.currentScope?.id)?.id ?? "";
  const [scopeId, setScopeId] = useState(initialScope);
  const [substituteId, setSubstituteId] = useState("");
  const [start, setStart] = useState(() => localDateTime(new Date()));
  const [end, setEnd] = useState(() =>
    localDateTime(new Date(Date.now() + 7 * 86400000)),
  );
  const [reason, setReason] =
    useState<DocumentSubstitution["reason"]>("vacation");
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const sending = useRef(false),
    selecting = useRef(false);
  const key = useSubmissionKey();
  const candidates = useQuery({
    queryKey: ["document-workflow", "substitution-candidates", scopeId, userId],
    queryFn: () => services.substitutions!.candidates(scopeId, userId),
    enabled: scopeId !== "" && !switching,
    retry: false,
  });
  async function selectScope(id: string) {
    if (selecting.current || pending) return;
    setScopeId(id);
    setSubstituteId("");
    if (
      id === "" ||
      bootstrap?.allOrganizations ||
      id === bootstrap?.currentScope?.id
    )
      return;
    selecting.current = true;
    setSwitching(true);
    setError(null);
    try {
      // Как в выдаче доступа, выбранный КПО становится рабочим контекстом.
      await services.administration.selectScope(id);
      await Promise.all([
        client.invalidateQueries({ queryKey: shellBootstrapKey }),
        client.invalidateQueries({ queryKey: ["document-workflow"] }),
      ]);
    } catch (failure) {
      setError(failure);
      setScopeId("");
    } finally {
      setSwitching(false);
      selecting.current = false;
    }
  }
  async function submit() {
    if (sending.current || switching) return;
    const invalid = substitutionPeriodErrors(start, end);
    if (!scopeId) invalid.access_scope_id = "Выберите КПО";
    if (!substituteId) invalid.substitute_id = "Выберите заместителя";
    if (substituteId === userId)
      invalid.substitute_id = "Нельзя назначить заместителем самого себя";
    if (note.length > 500) invalid.note = "Максимум 500 символов";
    setErrors(invalid);
    if (Object.keys(invalid).length > 0) return;
    sending.current = true;
    setPending(true);
    setError(null);
    try {
      await services.substitutions!.create(
        {
          user_id: userId,
          substitute_id: substituteId,
          access_scope_id: scopeId,
          starts_at: new Date(start).toISOString(),
          ends_at: new Date(end).toISOString(),
          reason,
          note: note.trim() || null,
        },
        key.current(),
      );
      setPending(false);
      key.reset();
      await Promise.all([
        client.invalidateQueries({ queryKey: ["document-workflow"] }),
        client.invalidateQueries({ queryKey: ["user", userId] }),
        client.invalidateQueries({ queryKey: ["users"] }),
      ]);
      onSaved();
    } catch (failure) {
      setPending(false);
      setError(failure);
      if (isValidationApiError(failure))
        setErrors(
          Object.fromEntries(
            Object.entries(failure.details.fields).map(([field, messages]) => [
              field,
              messages[0] ?? "Проверьте поле",
            ]),
          ),
        );
    } finally {
      sending.current = false;
    }
  }
  return (
    <form
      className="cp-card approval-form"
      noValidate
      onChange={() => {
        key.reset();
        setError(null);
        setErrors({});
      }}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <h3>Назначить заместителя</h3>
      {error ? <Notice>{substitutionError(error)}</Notice> : null}
      <fieldset disabled={pending || switching}>
        <label className="field">
          <span>КПО</span>
          <Select
            value={scopeId}
            onChange={(event) => void selectScope(event.target.value)}
          >
            <option value="">Выберите КПО</option>
            {scopes.map((scope) => (
              <option key={scope.id} value={scope.id}>
                {scope.name}
              </option>
            ))}
          </Select>
        </label>
        {errors.access_scope_id ? (
          <small className="field-error" role="alert">
            {errors.access_scope_id}
          </small>
        ) : null}
        {switching ? <p aria-busy="true">Переключаем рабочий КПО…</p> : null}
        {candidates.isLoading ? (
          <p aria-busy="true">Загружаем заместителей…</p>
        ) : null}
        {candidates.isError ? (
          <Notice>
            Не удалось загрузить заместителей.{" "}
            <button type="button" onClick={() => void candidates.refetch()}>
              Повторить
            </button>
          </Notice>
        ) : null}
        <label className="field">
          <span>Заместитель</span>
          <Select
            value={substituteId}
            onChange={(event) => setSubstituteId(event.target.value)}
            disabled={!scopeId || candidates.isLoading || candidates.isError}
          >
            <option value="">Выберите заместителя</option>
            {candidates.data
              ?.filter((person) => person.id !== userId)
              .map((person) => (
                <option key={person.id} value={person.id}>
                  {person.display_name}
                </option>
              ))}
          </Select>
        </label>
        {/* Заместителем может быть только подчинённый: без иерархии список
            пуст, и человеку нужно объяснить, что делать дальше. */}
        {candidates.data &&
        candidates.data.filter((person) => person.id !== userId).length ===
          0 ? (
          <Notice>
            В этом КПО нет подчинённых, которых можно назначить заместителем.
            Попросите руководителя или администратора назначить замещение за
            вас.
          </Notice>
        ) : null}
        {errors.substitute_id ? (
          <small className="field-error" role="alert">
            {errors.substitute_id}
          </small>
        ) : null}
        <TextField
          label="Начало"
          type="datetime-local"
          value={start}
          onChange={setStart}
          error={errors.starts_at}
        />
        <TextField
          label="Конец"
          type="datetime-local"
          value={end}
          onChange={setEnd}
          error={errors.ends_at}
        />
        <label className="field">
          <span>Причина</span>
          <Select
            value={reason}
            onChange={(event) => {
              const value = event.target.value;
              if (
                value === "vacation" ||
                value === "business_trip" ||
                value === "sick_leave" ||
                value === "other"
              )
                setReason(value);
            }}
          >
            {Object.entries(substitutionReasons).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </label>
        <TextField
          label="Заметка"
          value={note}
          onChange={setNote}
          maxLength={500}
          multiline
          error={errors.note}
        />
        <div className="approval-actions">
          <button
            className="button button--primary"
            type="submit"
            disabled={
              pending ||
              switching ||
              !scopeId ||
              candidates.isLoading ||
              candidates.isError
            }
          >
            Сохранить замещение
          </button>
          <button
            className="button button--secondary"
            type="button"
            onClick={onCancel}
          >
            Отмена
          </button>
        </div>
      </fieldset>
    </form>
  );
}
